<script setup lang="ts">
import { computed, ref } from 'vue'
import { useNewsletterSubscribeMutation } from '~/composables/queries/useStoreApi'
import { useNotifications } from '~/composables/useNotifications'
import { newsletterDiscountPercent, type NewsletterCampaign } from '~/utils/newsletterOffer'

const props = defineProps<{
  /**
   * Sekcja jest jedyną treścią strony — landing kampanii `/newsletter`, bez
   * nagłówka i stopki serwisu. Tytuł staje się nagłówkiem strony (h1),
   * polityka prywatności otwiera się w nowej karcie, żeby nie zgubić
   * wpisanego adresu, a po zapisie pojawia się droga do sklepu. Brak = sekcja
   * w treści strony (home, kategorie), jak dotąd.
   */
  standalone?: boolean
  /** Kampania z reklamy: hasło, grafika i tag edrone zamiast treści marki. */
  campaign?: NewsletterCampaign
}>()

const brand = useBrand()
const { sellingEnabled } = useCatalogOnly()
const isYoulook = computed(() => brand.variants?.newsletterLayout === 'youlook')
// Dwie kolumny (tekst + grafika) ma wariant youlook i każda kampania z grafiką;
// `newsletter--youlook` niesie już tylko wygląd marki.
const layoutClass = computed(() => ({
  'newsletter--youlook': isYoulook.value,
  'newsletter--split': isYoulook.value || !!props.campaign,
  'newsletter--campaign': !!props.campaign,
}))
// Sklep katalogowy nie prowadzi promocji ani wyprzedaży, więc obietnica
// „informacji o trendach i promocjach" byłaby pusta. Zapis ma sens, ale
// dotyczy nowości w katalogu.
const isCatalog = computed(() => !sellingEnabled)
const headingTag = computed(() => (props.standalone ? 'h1' : 'h2'))
// Czasowa podmiana rabatu (np. 40% do 15.11) przełącza się sama po dacie końca —
// liczona w chwili renderu, jak start akcji kodów progowych (useStandingPromo).
const discountPercent = computed(() =>
  newsletterDiscountPercent(brand.newsletterDiscount ?? { percent: 20 }, Date.now()),
)
const emailId = useId()

const email = ref('')
const consent = ref(false)
const subscribed = ref(false)

const subscribe = useNewsletterSubscribeMutation()
const notifications = useNotifications()
const analytics = useAnalytics()

const isPending = subscribe.isPending

async function onSubmit() {
  const value = email.value.trim()
  if (!value || !value.includes('@')) {
    notifications.error('Podaj prawidłowy adres e-mail.')
    return
  }
  if (!consent.value) {
    notifications.error(isCatalog.value
      ? 'Aby się zapisać, zaznacz zgodę na otrzymywanie newslettera.'
      : 'Aby kontynuować, zaakceptuj politykę prywatności.')
    return
  }
  try {
    await subscribe.mutateAsync({ email: value })
    subscribed.value = true
    // edrone rozróżnia źródło zapisu tagiem (Footer / Register / Order) — zapisy
    // z landingu dostają własny, a z kampanii tag, na którym marketing zbiera grupę.
    // Kampania bez tagu (null) = zapis podpina samo edrone, więc go nie wysyłamy.
    analytics.trackNewsletterSubscribe(
      value,
      props.campaign ? props.campaign.edroneTag : (props.standalone ? 'Landing' : 'Footer'),
    )
  }
  catch (error: unknown) {
    const status = (error as { statusCode?: number })?.statusCode
    if (status === 409) {
      notifications.error('Ten adres e-mail jest już zapisany do newslettera.')
    }
    else if (status === 400) {
      notifications.error('Adres e-mail jest nieprawidłowy.')
    }
    else {
      notifications.error('Nie udało się zapisać. Spróbuj ponownie później.')
    }
  }
}
</script>

<template>
  <section class="newsletter" :class="layoutClass">
    <div class="newsletter__inner container">
      <div class="newsletter__head">
        <component :is="headingTag" class="newsletter__title">
          <template v-if="campaign">{{ campaign.title }}</template>
          <template v-else-if="isCatalog">Nowości w katalogu gadżetów</template>
          <template v-else-if="isYoulook">
            <strong>{{ discountPercent }}% zniżki</strong> na pierwsze zamówienie!
          </template>
          <template v-else>Zapisz się do naszego newslettera!</template>
        </component>
        <p v-if="campaign" class="newsletter__subtitle">
          {{ campaign.text }}
        </p>
        <p v-else-if="isCatalog" class="newsletter__subtitle">
          Napiszemy, gdy pojawią się nowe produkty i pomysły na upominki firmowe.
          Bez ofert handlowych — tylko katalog.
        </p>
        <p v-else-if="isYoulook" class="newsletter__subtitle">
          Zapisz się do naszego newslettera, aby otrzymywać najnowsze
          informacje o trendach, promocjach i nie tylko!
        </p>
        <p v-else class="newsletter__subtitle">
          Bądź pierwszym, który otrzyma najświeższe informacje o trendach,
          promocjach i nie tylko!
        </p>
      </div>
      <!-- Umowa z edrone (youlook /newsletter, One-Click): ich kod czyta zapis po
           `form.newsletter__form`, `input[type="email"]` i bloku `.newsletter__done`
           po potwierdzeniu. Zmiana tych selektorów odcina zapisy od edrone —
           uzgodnij ją z edrone (pilnuje tego newsletter-landing-page.test.ts). -->
      <form
        v-if="!subscribed"
        class="newsletter__form"
        @submit.prevent="onSubmit"
      >
        <input
          :id="emailId"
          v-model="email"
          type="email"
          placeholder="Twój adres e-mail"
          class="field newsletter__input"
          aria-label="Adres e-mail"
          required
          :disabled="isPending"
        >
        <label class="newsletter__consent">
          <input
            v-model="consent"
            type="checkbox"
            class="newsletter__consent-input"
          >
          <span class="newsletter__checkbox" :class="{ 'is-checked': consent }">
            <svg
              v-if="consent"
              width="10"
              height="8"
              viewBox="0 0 10 8"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 4l2.5 2.5L9 1"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <!-- Katalog (proles): wyraźna zgoda na informacje handlowe e-mailem
               (Prawo komunikacji elektronicznej) — samo „akceptuję politykę”
               jej nie zastępuje; tak opisuje to polityka prywatności proles.
               Pozostałe marki bez zmian. -->
          <span v-if="isCatalog">
            *Chcę otrzymywać na podany adres e-mail newsletter {{ brand.name }}
            z informacjami handlowymi o nowościach w katalogu i pomysłach na upominki
            firmowe. Zgodę mogę wycofać w każdej chwili. Więcej w
            <NuxtLink
              to="/polityka-prywatnosci"
              :target="standalone ? '_blank' : undefined"
            >polityce prywatności</NuxtLink>.
          </span>
          <span v-else>
            *Akceptuję i rozumiem
            <NuxtLink
              to="/polityka-prywatnosci"
              :target="standalone ? '_blank' : undefined"
            >politykę prywatności</NuxtLink>
          </span>
        </label>
        <button
          type="submit"
          class="btn btn-primary newsletter__btn"
          :disabled="isPending"
        >
          <template v-if="isPending">Zapisuję…</template>
          <template v-else-if="campaign">
            {{ campaign.cta }} <span aria-hidden="true">→</span>
          </template>
          <template v-else>Zapisz się</template>
        </button>
      </form>
      <!-- Podziękowanie i droga do sklepu w jednym bloku: w dwóch kolumnach
           zajmują jedną komórkę, inaczej przycisk spadałby pod wysoką grafikę. -->
      <div v-else class="newsletter__done">
        <p class="newsletter__confirm">
          Dziękujemy! Zapisaliśmy Cię do newslettera.
        </p>
        <NuxtLink
          v-if="standalone"
          to="/"
          class="btn btn-primary newsletter__btn newsletter__shop"
        >
          {{ isCatalog ? 'Zobacz katalog' : 'Przejdź do sklepu' }}
        </NuxtLink>
      </div>
      <!-- Grafika kampanii ma narysowany przycisk „Dołącz teraz", więc jest
           etykietą pola e-mail: kliknięcie ustawia kursor w polu (na telefonie
           z klawiaturą). Pion na telefon, kwadrat na szersze ekrany. -->
      <label
        v-if="campaign"
        class="newsletter__media"
        :for="subscribed ? undefined : emailId"
      >
        <picture>
          <source
            media="(max-width: 600px)"
            :srcset="campaign.image.vertical.src"
            :width="campaign.image.vertical.width"
            :height="campaign.image.vertical.height"
          >
          <img
            :src="campaign.image.square.src"
            :alt="campaign.image.alt"
            :width="campaign.image.square.width"
            :height="campaign.image.square.height"
            fetchpriority="high"
          >
        </picture>
      </label>
      <img
        v-else-if="isYoulook"
        class="newsletter__media"
        src="/brands/youlook/newsletter.png"
        alt=""
        width="910"
        height="252"
        loading="lazy"
      >
    </div>
  </section>
</template>

<style scoped>
.newsletter {
  background: var(--color-white);
  padding: 48px 0;
}

.newsletter__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

.newsletter__head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  text-align: center;
}

.newsletter__title {
  margin: 0;
  font-size: 2.25rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  text-transform: uppercase;
  line-height: 1;
}

.newsletter__subtitle {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
}

.newsletter__form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 676px;
}

.newsletter__input {
  width: 100%;
}

.newsletter__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.newsletter__consent {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
}

.newsletter__consent a {
  text-decoration: underline;
  color: inherit;
}

.newsletter__consent-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.newsletter__checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid var(--color-black);
  border-radius: 4px;
  flex-shrink: 0;
  background: var(--color-white);
  color: var(--color-white);
  transition: background var(--transition-fast, 150ms ease);
}

.newsletter__consent-input:focus-visible + .newsletter__checkbox {
  outline: 2px solid var(--color-black);
  outline-offset: 2px;
}

.newsletter__checkbox.is-checked {
  background: var(--color-black);
}

.newsletter__btn {
  padding: 10px 50px;
}

.newsletter__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.newsletter__confirm {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  text-align: center;
}

/* 40 px = dawny odstęp potwierdzenia od przycisku, gdy oba były osobnymi
   dziećmi .newsletter__inner; w dwóch kolumnach 24 px jak row-gap siatki. */
.newsletter__done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

/* ── Dwie kolumny: tekst i formularz obok grafiki (youlook, kampanie) ── */
.newsletter--split .newsletter__inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: clamp(32px, 5vw, 80px);
  row-gap: 24px;
  align-content: center;
}

.newsletter--split .newsletter__head {
  grid-column: 1;
  grid-row: 1;
  align-items: flex-start;
  text-align: left;
}

.newsletter--split .newsletter__subtitle {
  line-height: 1.4;
}

.newsletter--split .newsletter__form,
.newsletter--split .newsletter__done {
  grid-column: 1;
  grid-row: 2;
  align-items: flex-start;
  text-align: left;
  max-width: none;
}

.newsletter--split .newsletter__done {
  gap: 24px;
}

.newsletter--split .newsletter__confirm {
  text-align: left;
}

.newsletter--split .newsletter__media {
  grid-column: 2;
  grid-row: 1 / 3;
  align-self: center;
  display: block;
  width: 100%;
  height: auto;
  border-radius: 15px;
}

/* ── Wygląd youlooka: promo rabatu w tytule, kanciaste pola ── */
.newsletter--youlook .newsletter__title {
  font-size: 1.75rem;
  line-height: 1.2;
}

.newsletter--youlook .newsletter__title strong {
  font-weight: var(--font-weight-bold);
}

.newsletter--youlook .newsletter__input {
  border-radius: 4px;
}

.newsletter--youlook .newsletter__checkbox {
  border-radius: 0;
}

/* ── Kampania: grafika z reklamy jest etykietą pola e-mail ── */
.newsletter--campaign .newsletter__media {
  overflow: hidden;
}

.newsletter--campaign .newsletter__media[for] {
  cursor: pointer;
}

.newsletter--campaign .newsletter__media img {
  display: block;
  width: 100%;
  height: auto;
}

/* Grafika kampanii jest wyższa niż tekst, więc rozpycha oba wiersze — hasło
   dosuwamy do dołu pierwszego, formularz do góry drugiego, żeby stały razem
   na wysokości środka grafiki. */
.newsletter--campaign .newsletter__head {
  align-self: end;
}

.newsletter--campaign .newsletter__form,
.newsletter--campaign .newsletter__done {
  align-self: start;
}

@media (max-width: 960px) {
  .newsletter {
    padding: 20px 0;
  }

  .newsletter__title {
    font-size: 1.5rem;
  }

  .newsletter--split .newsletter__inner {
    grid-template-columns: minmax(0, 1fr);
  }

  .newsletter--split .newsletter__head,
  .newsletter--split .newsletter__form,
  .newsletter--split .newsletter__done,
  .newsletter--split .newsletter__media {
    grid-column: 1;
    grid-row: auto;
  }

  /* Klientka przychodzi z reklamy — na telefonie najpierw ta sama grafika,
     pod nią hasło i formularz. */
  .newsletter--campaign .newsletter__media {
    order: -1;
    justify-self: center;
    max-width: 560px;
  }
}
</style>
